// src/components/onboarding/rubric-results.tsx
//
// Reusable rubric-results viewer. Reads the per-response scoring detail that
// already exists behind /runs/{id}/scoring/scores and renders, on ONE screen:
//
//   * a per-variable summary table — average score, count of 0s / 1s / 2s, and
//     a per-model average column for every model in the run;
//   * a per-response drill-down — filter by variable / model / query text and
//     read each judged response's 0/1/2 per variable next to the judge's
//     evidence sentence (evidence is already stored, we just show it);
//   * the run parameters (judge, rubric version, models, reps, queries, …);
//   * a client-side CSV export of the per-variable summary.
//
// No new backend: this is a frontend read of existing data. Mounted in the
// Stage 5 scoring result, the Stage 11 per-run view, and the Stage 4 smoke
// calibration review (the go/no-go gate must show what the rubric actually did
// BEFORE the full battery can start).
//
import React, { useEffect, useMemo, useState } from "react";
import {
  Loader2, ListChecks, FileText, Download, ChevronDown, ChevronRight, AlertTriangle,
} from "lucide-react";
import { listResponseScores, type ResponseScore } from "@/api/onboarding-scoring-api";

export interface RubricRunParam {
  label: string;
  value: string;
}

interface Props {
  customerId: number;
  runId: number;
  /** Framework reasoning variables — supplies the human-readable claim text and
   *  the canonical variable order. Falls back to the union of row keys. */
  reasoningVariables?: { id: string; claim?: string }[];
  /** Pin the scoring pass to one rubric version. A run can hold multiple passes
   *  (e.g. after a rubric-change rescore, old + new rubric rows coexist); without
   *  this the viewer fetches ALL passes and blends them (and pays the extra
   *  payload). Pass the run's current/baseline rubric to show just that pass. */
  rubricVersion?: string;
  /** Pin the pass to one JUDGE as well. Rubric alone separates a rubric-change
   *  rescore, but not a run judged twice on the SAME rubric (run 29: opus and
   *  sonnet, both fw-58be7e3b) — without this the table blends two judges. */
  scorerModel?: string;
  /** Chips shown in the header: judge, rubric version, models, reps, queries. */
  runParams?: RubricRunParam[];
  /** Measurement-only lanes excluded from the blended average (e.g. gptweb),
   *  mirroring the backend rollup. They still appear as a per-model column. */
  excludedModels?: string[];
  title?: string;
  defaultExpanded?: boolean;
}

const PAGE = 25;

export function RubricResults({
  customerId,
  runId,
  reasoningVariables,
  rubricVersion,
  scorerModel,
  runParams,
  excludedModels = ["gptweb"],
  title = "Rubric results",
  defaultExpanded = true,
}: Props) {
  const [rows, setRows] = useState<ResponseScore[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(defaultExpanded);

  const [filterVar, setFilterVar] = useState<string>("");
  const [filterModel, setFilterModel] = useState<string>("");
  const [queryFilter, setQueryFilter] = useState<string>("");
  const [visible, setVisible] = useState(PAGE);

  // Lazy-load the per-response rows the first time the panel is expanded. A run
  // can have ~1k rows and the /scoring/scores endpoint caps `limit` at 500, so
  // we page through it and aggregate client-side (no backend change). When
  // rubricVersion + scorerModel are set the server returns only that ONE pass,
  // so a twice-judged run isn't 2x the rows and the per-variable table isn't
  // blended across judges or rubrics.
  //
  // Deps are ONLY the fetch identity (expanded + run + rubric). Crucially, `rows`
  // and `loading` are NOT deps: putting them here caused setLoading(true) to
  // re-run the effect, whose cleanup set cancelled=true and aborted the paging
  // loop after page 0 — and since `finally` skips the reset when cancelled,
  // `loading` stuck true forever (infinite spinner, page 2 never fetched). With
  // stable deps, an unrelated parent re-render can no longer cancel an in-flight
  // load. `fetchKey` re-arms the load when the identity changes (run/rubric).
  const fetchKey = `${customerId}:${runId}:${rubricVersion || ""}:${scorerModel || ""}`;
  useEffect(() => {
    if (!expanded) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setRows(null);
    (async () => {
      try {
        const PAGE_SIZE = 500;
        const all: ResponseScore[] = [];
        // Safety cap (40 pages = 20k rows) so a pathological run can't loop.
        for (let offset = 0; offset < 20000; offset += PAGE_SIZE) {
          const r = await listResponseScores(customerId, runId, {
            limit: PAGE_SIZE, offset, rubricVersion, scorerModel,
          });
          if (cancelled) return;
          all.push(...r.items);
          if (r.items.length < PAGE_SIZE) break;
        }
        if (!cancelled) setRows(all);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || "Failed to load scores");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded, fetchKey]);

  const claimOf = useMemo(() => {
    const map = new Map<string, string>();
    (reasoningVariables || []).forEach((v) => map.set(v.id, v.claim || v.id));
    return (id: string) => map.get(id) || id;
  }, [reasoningVariables]);

  const rvIds = useMemo(() => {
    if (reasoningVariables?.length) return reasoningVariables.map((v) => v.id);
    const seen = new Set<string>();
    (rows || []).forEach((r) => Object.keys(r.rv_scores || {}).forEach((k) => seen.add(k)));
    return Array.from(seen);
  }, [reasoningVariables, rows]);

  const models = useMemo(() => {
    const seen = new Set<string>();
    (rows || []).forEach((r) => seen.add(r.model || "unknown"));
    return Array.from(seen).sort();
  }, [rows]);

  // Run parameters derived straight from the scored rows — judge model, rubric
  // version, response count, models — so every run card shows them without the
  // parent having to plumb them through. Parent-supplied runParams (e.g. sample
  // size, query count) are merged in, minus any label the derived set already
  // covers.
  const allParams = useMemo<RubricRunParam[]>(() => {
    const derived: RubricRunParam[] = [];
    if (rows && rows.length > 0) {
      const first = rows[0];
      if (first.scorer_model) derived.push({ label: "Judge", value: first.scorer_model });
      if (first.rubric_version) derived.push({ label: "Rubric", value: first.rubric_version });
      derived.push({ label: "Responses", value: String(rows.length) });
      if (models.length) derived.push({ label: "Models", value: models.join(", ") });
    }
    const seen = new Set(derived.map((p) => p.label));
    return [...derived, ...(runParams || []).filter((p) => !seen.has(p.label))];
  }, [rows, models, runParams]);

  const perVariable = useMemo(() => {
    const excluded = new Set(excludedModels);
    return rvIds.map((id) => {
      let n = 0, sum = 0, c0 = 0, c1 = 0, c2 = 0;
      const modelSum: Record<string, number> = {};
      const modelN: Record<string, number> = {};
      (rows || []).forEach((r) => {
        const raw = r.rv_scores?.[id];
        if (raw === undefined || raw === null) return;
        const value = Number(raw);
        if (Number.isNaN(value)) return;
        const model = r.model || "unknown";
        modelSum[model] = (modelSum[model] || 0) + value;
        modelN[model] = (modelN[model] || 0) + 1;
        // The blended average excludes measurement-only lanes so it matches the
        // headline rollup; per-model columns still show every lane.
        if (excluded.has(model)) return;
        n += 1;
        sum += value;
        if (value === 0) c0 += 1;
        else if (value === 1) c1 += 1;
        else if (value === 2) c2 += 1;
      });
      const perModel: Record<string, number | null> = {};
      models.forEach((m) => (perModel[m] = modelN[m] ? modelSum[m] / modelN[m] : null));
      return { id, claim: claimOf(id), n, avg: n ? sum / n : 0, c0, c1, c2, perModel };
    });
  }, [rvIds, rows, models, excludedModels, claimOf]);

  const filtered = useMemo(() => {
    const q = queryFilter.trim().toLowerCase();
    return (rows || []).filter((r) => {
      if (filterModel && (r.model || "unknown") !== filterModel) return false;
      if (q && !(r.query_text || "").toLowerCase().includes(q)) return false;
      if (filterVar && r.rv_scores?.[filterVar] === undefined) return false;
      return true;
    });
  }, [rows, filterModel, queryFilter, filterVar]);

  useEffect(() => { setVisible(PAGE); }, [filterVar, filterModel, queryFilter]);

  const exportCsv = () => {
    const header = [
      "variable_id", "claim", "avg", "count_0", "count_1", "count_2", "n",
      ...models.map((m) => `avg_${m}`),
    ];
    const lines = [header.map(csvCell).join(",")];
    perVariable.forEach((pv) => {
      lines.push([
        pv.id, pv.claim, pv.avg.toFixed(4), pv.c0, pv.c1, pv.c2, pv.n,
        ...models.map((m) => (pv.perModel[m] == null ? "" : (pv.perModel[m] as number).toFixed(4))),
      ].map(csvCell).join(","));
    });
    downloadCsv(`rubric-summary-run-${runId}.csv`, lines.join("\n"));
  };

  // Full per-response dump: one row per scored response, with the 0/1/2 score
  // AND the judge's evidence sentence for every reasoning variable, plus the
  // mention-lane fields. Exports the currently-filtered set (all rows when no
  // filter is applied), so it always reflects exactly what's on screen. Uses
  // the loaded rows — no extra fetch.
  const exportEvidenceCsv = () => {
    const header = [
      "response_index", "model", "query_text",
      "mentioned", "in_top5", "primary_rec", "number_one",
      "scoring_status", "mention_evidence",
      ...rvIds.flatMap((id) => [`${id}__score`, `${id}__evidence`]),
    ];
    const lines = [header.map(csvCell).join(",")];
    filtered.forEach((r) => {
      lines.push([
        r.response_index, r.model || "", stripMarkdown(r.query_text),
        r.scorer_mentioned ? 1 : 0, r.scorer_in_top5 ? 1 : 0,
        r.scorer_primary_rec ? 1 : 0, r.scorer_number_one ? 1 : 0,
        r.scoring_status, stripMarkdown(r.mention_evidence),
        ...rvIds.flatMap((id) => [
          r.rv_scores?.[id] ?? "",
          stripMarkdown(r.rv_evidence?.[id]),
        ]),
      ].map(csvCell).join(","));
    });
    downloadCsv(`rubric-evidence-run-${runId}.csv`, lines.join("\n"));
  };

  return (
    <div className="rounded-xl p-4 md:p-5" style={{ backgroundColor: "var(--card)", border: "1px solid var(--border)" }}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <button
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-2"
          style={{ background: "transparent", border: "none", cursor: "pointer", padding: 0 }}
        >
          {expanded ? <ChevronDown className="w-4 h-4" style={{ color: "var(--muted-foreground)" }} />
            : <ChevronRight className="w-4 h-4" style={{ color: "var(--muted-foreground)" }} />}
          <ListChecks className="w-4 h-4" style={{ color: "var(--alpha-teal)" }} />
          <span style={{ fontSize: "var(--text-base)", fontWeight: 600, color: "var(--foreground)" }}>{title}</span>
        </button>
        {expanded && rows && rows.length > 0 && (
          <button
            onClick={exportCsv}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg flex-shrink-0"
            style={{ background: "transparent", color: "var(--signal-blue)", fontSize: "var(--text-xs)", fontWeight: 600, border: "1px solid var(--signal-blue)", cursor: "pointer" }}
          >
            <Download className="w-3.5 h-3.5" /> Summary CSV
          </button>
        )}
      </div>

      {/* Run parameters — visible on every run card (judge + rubric derived
          from the scored rows; sample/query counts from the parent). */}
      {allParams.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {allParams.map((p) => (
            <span key={p.label} style={paramChip}>
              <span style={{ color: "var(--muted-foreground)" }}>{p.label}:</span>{" "}
              <strong style={{ color: "var(--foreground)" }}>{p.value}</strong>
            </span>
          ))}
        </div>
      )}

      {!expanded && (
        <p style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", margin: 0 }}>
          Expand to review per-variable scores, distribution, per-model breakdown, and the judge’s
          evidence for every response.
        </p>
      )}

      {expanded && (
        <>
          {loading && (
            <div className="flex items-center gap-2 py-4" style={{ color: "var(--muted-foreground)", fontSize: "var(--text-sm)" }}>
              <Loader2 className="w-4 h-4 animate-spin" /> Loading scored responses…
            </div>
          )}
          {error && (
            <div className="flex items-center gap-2 py-3" style={{ color: "var(--infrared-coral)", fontSize: "var(--text-sm)" }}>
              <AlertTriangle className="w-4 h-4" /> {error}
            </div>
          )}
          {rows && rows.length === 0 && !loading && (
            <p style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>
              No scored responses for this run yet.
            </p>
          )}

          {rows && rows.length > 0 && (
            <>
              {/* Per-variable summary table */}
              <div style={{ overflowX: "auto", marginBottom: 20 }}>
                <table style={{ borderCollapse: "collapse", width: "100%", fontSize: "var(--text-xs)" }}>
                  <thead>
                    <tr>
                      <th style={{ ...th, textAlign: "left", minWidth: 220 }}>Reasoning variable</th>
                      <th style={th}>Avg</th>
                      <th style={th}>#0</th>
                      <th style={th}>#1</th>
                      <th style={th}>#2</th>
                      <th style={th}>n</th>
                      {models.map((m) => (
                        <th key={m} style={th} title={`Average for ${m}`}>{m}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {perVariable.map((pv) => {
                      const weak = pv.avg < 0.6;
                      return (
                        <tr key={pv.id}>
                          <td style={{ ...td, textAlign: "left" }} title={pv.id}>
                            <span style={{ color: "var(--foreground)" }}>{pv.claim}</span>
                          </td>
                          <td style={{ ...td, fontWeight: 700, color: weak ? "var(--infrared-coral)" : "var(--foreground)" }}>
                            {pv.avg.toFixed(2)}
                          </td>
                          <td style={{ ...td, color: pv.c0 ? "var(--infrared-coral)" : "var(--muted-foreground)" }}>{pv.c0}</td>
                          <td style={td}>{pv.c1}</td>
                          <td style={{ ...td, color: pv.c2 ? "var(--alpha-teal)" : "var(--muted-foreground)" }}>{pv.c2}</td>
                          <td style={{ ...td, color: "var(--muted-foreground)" }}>{pv.n}</td>
                          {models.map((m) => (
                            <td key={m} style={{ ...td, color: "var(--muted-foreground)" }}>
                              {pv.perModel[m] == null ? "—" : (pv.perModel[m] as number).toFixed(2)}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Per-response drill-down with evidence */}
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4" style={{ color: "var(--alpha-teal)" }} />
                  <span style={{ fontSize: "var(--text-sm)", fontWeight: 600 }}>Per-response evidence</span>
                </div>
                <button
                  onClick={exportEvidenceCsv}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg flex-shrink-0"
                  style={{ background: "transparent", color: "var(--signal-blue)", fontSize: "var(--text-xs)", fontWeight: 600, border: "1px solid var(--signal-blue)", cursor: "pointer" }}
                  title="Download every response with its per-variable score and evidence sentence (respects the filters below)"
                >
                  <Download className="w-3.5 h-3.5" /> Evidence CSV ({filtered.length})
                </button>
              </div>
              <div className="flex flex-wrap gap-2 mb-3">
                <select value={filterVar} onChange={(e) => setFilterVar(e.target.value)} style={selectStyle}>
                  <option value="">All variables</option>
                  {rvIds.map((id) => <option key={id} value={id}>{claimOf(id)}</option>)}
                </select>
                <select value={filterModel} onChange={(e) => setFilterModel(e.target.value)} style={selectStyle}>
                  <option value="">All models</option>
                  {models.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
                <input
                  value={queryFilter}
                  onChange={(e) => setQueryFilter(e.target.value)}
                  placeholder="Filter by query text…"
                  style={{ ...selectStyle, minWidth: 200, flex: 1 }}
                />
              </div>

              <div className="space-y-2">
                {filtered.slice(0, visible).map((r) => {
                  const shownVars = filterVar ? [filterVar] : rvIds.filter((id) => r.rv_scores?.[id] !== undefined);
                  return (
                    <div key={r.response_index} className="p-3 rounded-lg" style={{ backgroundColor: "var(--muted)", border: "1px solid var(--border)" }}>
                      <div className="flex items-center gap-2 mb-1 flex-wrap" style={{ fontSize: 11 }}>
                        <span style={badge("var(--muted-foreground)")}>{r.model || "unknown"}</span>
                        {r.scorer_mentioned && <span style={badge("var(--alpha-teal)")}>mentioned</span>}
                        {r.scoring_status === "failed_zeroed" && <span style={badge("var(--infrared-coral)")}>zeroed</span>}
                        {r.scoring_status === "excluded_no_answer" && <span style={badge("var(--muted-foreground)")} title="No answer from the measurement call; outside every rate">excluded</span>}
                        {r.query_text && (
                          <span style={{ color: "var(--muted-foreground)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 420 }} title={r.query_text}>
                            {r.query_text}
                          </span>
                        )}
                      </div>
                      <div className="space-y-1">
                        {shownVars.map((id) => {
                          const score = Number(r.rv_scores?.[id] ?? 0);
                          const evidence = r.rv_evidence?.[id];
                          return (
                            <div key={id} className="flex items-start gap-2" style={{ fontSize: "var(--text-xs)" }}>
                              <span style={scoreBadge(score)} title={`${claimOf(id)} = ${score}`}>{score}</span>
                              <span style={{ flex: 1, color: "var(--foreground)", lineHeight: 1.5 }}>
                                <strong style={{ color: "var(--muted-foreground)" }}>{claimOf(id)}:</strong>{" "}
                                {evidence ? `“${evidence}”` : <span style={{ color: "var(--muted-foreground)" }}>no evidence captured</span>}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex items-center justify-between mt-3" style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
                <span>Showing {Math.min(visible, filtered.length)} of {filtered.length} responses</span>
                {visible < filtered.length && (
                  <button
                    onClick={() => setVisible((v) => v + PAGE)}
                    className="px-3 py-1.5 rounded-lg"
                    style={{ background: "transparent", color: "var(--signal-blue)", border: "1px solid var(--border)", cursor: "pointer", fontSize: "var(--text-xs)", fontWeight: 600 }}
                  >
                    Show more
                  </button>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

// ─── CSV helpers (client-side; mirrors triggerBlobDownload) ──────────────────

function csvCell(value: unknown): string {
  const s = String(value ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

// Evidence sentences are quoted from model responses that often carry markdown
// (**bold**, *italic*, `code`, [links](url), ~~strike~~). For a plain-text CSV
// we want the raw readable content, so strip the inline formatting markers while
// keeping the actual words, numbers, and punctuation (e.g. "**2. Sunbit** —
// Excellent…" -> "2. Sunbit — Excellent…"). Content-level markers only; we don't
// touch leading list/heading structure so ordered items like "2." survive.
function stripMarkdown(input: unknown): string {
  let s = String(input ?? "");
  if (!s) return s;
  s = s.replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1"); // [text](url) / ![alt](url) -> text
  s = s.replace(/(\*\*\*|\*\*|\*|`|~~|__)/g, "");   // bold / italic / code / strikethrough markers
  return s;
}

function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const th: React.CSSProperties = {
  padding: "6px 10px", textAlign: "right", fontWeight: 600,
  color: "var(--muted-foreground)", borderBottom: "1px solid var(--border)",
  whiteSpace: "nowrap", position: "sticky", top: 0, backgroundColor: "var(--card)",
};
const td: React.CSSProperties = {
  padding: "6px 10px", textAlign: "right", borderBottom: "1px solid var(--border)",
  whiteSpace: "nowrap",
};
const selectStyle: React.CSSProperties = {
  padding: "6px 10px", fontSize: "var(--text-xs)", color: "var(--foreground)",
  backgroundColor: "var(--background)", border: "1px solid var(--border)",
  borderRadius: 8, outline: "none", fontFamily: "inherit",
};
const paramChip: React.CSSProperties = {
  padding: "3px 9px", borderRadius: 999, fontSize: 11,
  backgroundColor: "var(--muted)", border: "1px solid var(--border)",
};
const badge = (c: string): React.CSSProperties => ({
  padding: "1px 7px", borderRadius: 999, fontSize: 10, fontWeight: 700,
  color: c, backgroundColor: "var(--card)", border: `1px solid ${c}`,
});
const scoreBadge = (score: number): React.CSSProperties => {
  const color = score === 2 ? "var(--alpha-teal)" : score === 1 ? "var(--chart-3)" : "var(--infrared-coral)";
  return {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    width: 18, height: 18, borderRadius: 4, flexShrink: 0,
    fontSize: 11, fontWeight: 700, color, border: `1px solid ${color}`,
    backgroundColor: "var(--card)",
  };
};
